import type {
  ForeclosureOrdinanceSummary,
  RegistrationRequirementQuestion,
} from "@/lib/foreclosure-ordinance-summary";
import { cn, formatDateNoTimezone } from "@/lib/utils";

/**
 * Tile chrome shared with the case summary tile so both stay the same height
 * and scroll their own contents instead of growing the dialog.
 */
export const FORECLOSURE_DETAIL_TILE_CLASS =
  "max-h-56 overflow-x-hidden overflow-y-auto rounded-md border p-4";

function historicLine(
  item: ForeclosureOrdinanceSummary["historic"][number],
): string {
  const date = formatDateNoTimezone(item.effectiveStartDate);
  const detail = item.label ?? item.ordinance;
  return detail ? `${date} — ${detail}` : date;
}

export function ForeclosureOrdinanceSummarySection({
  isLoading,
  summaries,
  fallbackJurisdictionName,
  requirementQuestionsByLicenseTypeId = {},
}: {
  isLoading: boolean;
  summaries: ForeclosureOrdinanceSummary[];
  fallbackJurisdictionName?: string | null;
  requirementQuestionsByLicenseTypeId?: Record<
    string,
    RegistrationRequirementQuestion[]
  >;
}) {
  const jurisdictionName =
    summaries[0]?.jurisdictionName || fallbackJurisdictionName?.trim() || null;

  return (
    <section className="shrink-0 space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground">
        Jurisdiction / ordinance
      </h3>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading ordinance…</p>
      ) : (
        <div className={cn(FORECLOSURE_DETAIL_TILE_CLASS, "space-y-3")}>
          {summaries.length === 0 ? (
            <dl className="grid gap-3 sm:grid-cols-3">
              <div>
                <dt className="text-xs font-medium text-muted-foreground">
                  Jurisdiction
                </dt>
                <dd className="text-sm">{jurisdictionName || "—"}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-xs font-medium text-muted-foreground">
                  Foreclosure ordinance
                </dt>
                <dd className="text-sm text-muted-foreground">
                  No foreclosure ordinance configured
                </dd>
              </div>
            </dl>
          ) : (
            summaries.map((summary) => (
              <dl
                key={`${summary.licenseTypeName}-${summary.ordinanceStartDate}`}
                className="grid gap-3 sm:grid-cols-3"
              >
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    Jurisdiction
                  </dt>
                  <dd className="text-sm">{summary.jurisdictionName || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    License type
                  </dt>
                  <dd className="text-sm">{summary.licenseTypeName}</dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-muted-foreground">
                    Ordinance start date
                  </dt>
                  <dd className="text-sm">
                    {formatDateNoTimezone(summary.ordinanceStartDate) || "—"}
                  </dd>
                </div>
                {summary.ordinance ? (
                  <div className="sm:col-span-3">
                    <dt className="text-xs font-medium text-muted-foreground">
                      Ordinance
                    </dt>
                    <dd className="text-sm">{summary.ordinance}</dd>
                  </div>
                ) : null}
                <div className="sm:col-span-3">
                  <dt className="text-xs font-medium text-muted-foreground">
                    Registration requirements
                  </dt>
                  <dd className="text-sm">
                    {(
                      requirementQuestionsByLicenseTypeId[
                        summary.licenseTypeId
                      ] ?? []
                    ).length === 0 ? (
                      "None"
                    ) : (
                      <ul className="space-y-2">
                        {(
                          requirementQuestionsByLicenseTypeId[
                            summary.licenseTypeId
                          ] ?? []
                        ).map((item) => (
                          <li key={item.id} className="space-y-1">
                            <p>{item.question}</p>
                            {item.informationText ? (
                              <div
                                className="text-muted-foreground [&>ul]:ml-4 [&>ul]:list-disc [&>ol]:ml-4 [&>ol]:list-decimal [&>p]:mb-2 [&>p:last-child]:mb-0"
                                // biome-ignore lint/security/noDangerouslySetInnerHtml: information_text is operator-authored HTML
                                dangerouslySetInnerHTML={{
                                  __html: item.informationText,
                                }}
                              />
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
                <div className="sm:col-span-3">
                  <dt className="text-xs font-medium text-muted-foreground">
                    Historic ordinances
                  </dt>
                  <dd className="text-sm">
                    {summary.historic.length === 0 ? (
                      "None"
                    ) : (
                      <ul className="list-disc space-y-1 pl-4">
                        {summary.historic.map((item) => (
                          <li key={item.id}>{historicLine(item)}</li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </div>
              </dl>
            ))
          )}
        </div>
      )}
    </section>
  );
}
